import React, { useEffect, useState } from 'react';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid2';
import OutlinedInput from '@mui/material/OutlinedInput';
import styled from '@mui/material/styles/styled';
import { useAppContext } from '../contexts/AppContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Box, Button } from '@mui/material';
import { Image } from '@mui/icons-material';


const FormGrid = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));


const AddressForm: React.FC<any> = ({ initialValues }) => {
  const { appState, updateAppState } = useAppContext();
  const { translations } = useLanguage(); // ✅ Get translations



  const [address, setAddress] = useState({
    storeName: initialValues?.storeName || '',
    address1: initialValues?.address1 || '',
    address2: initialValues?.address2 || '',
    city: initialValues?.city || '',
    postalCode: initialValues?.postalCode || '',
    country: initialValues?.country || '',
    phone: initialValues?.phone || '',
    website: initialValues?.website || '',
    logo: initialValues?.logo || '',
  });

  // also update the global state with initial values
  useEffect(() => {
    updateAppState({ newStoreAddress: address });
  }, []);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match('image.*')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (limit to 1MB)
    if (file.size > 1024 * 1024) {
      alert('File size should be less than 1MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      setAddress({ ...address, logo: base64 });
    };
    reader.readAsDataURL(file);
  };


  useEffect(() => {
    updateAppState({ newStoreAddress: address });
  }, [address]);

  const handlePhoneChange = (value: string) => {
    const phone = value.replace(/[^0-9+]/g, '');
    setAddress({ ...address, phone: phone });
  };

  return (
    <Grid container spacing={3}>
      <FormGrid size={{ xs: 12, md: 12, lg: 12 }}>
        <FormLabel htmlFor="store-name" required>
          {translations.store_creation_section.store_name}
        </FormLabel>
        <OutlinedInput
          id="store-name"
          name="store-name"
          type="name"
          placeholder={translations.store_creation_section.store_name_placeholder}
          autoComplete="store name"
          required
          size="small"
          value={address.storeName}
          onChange={(e) => setAddress({ ...address, storeName: e.target.value })}
        />
      </FormGrid>
      <FormGrid size={{ xs: 12 }}>
        <FormLabel htmlFor="phone" required>
          {translations.store_creation_section.phone}
        </FormLabel>
        <OutlinedInput
          id="phone"
          name="phone"
          type="tel"
          placeholder={translations.store_creation_section.phone_placeholder}
          autoComplete="tel"
          required
          size="small"
          value={address.phone}
          onChange={(e) => handlePhoneChange(e.target.value)}
        />
      </FormGrid>
      <FormGrid size={{ xs: 12 }}>
        {translations.store_creation_section.website}
        <OutlinedInput
          id="website"
          name="website"
          type="url"
          placeholder={translations.store_creation_section.website_placeholder}
          autoComplete="url"
          size="small"
          value={address.website}
          onChange={(e) => setAddress({ ...address, website: e.target.value })}
        />
      </FormGrid>
      <FormGrid size={{ xs: 12 }}>
        <FormLabel htmlFor="logo">
          {translations.store_creation_section.logo || "Logo"}
        </FormLabel>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <input
            accept="image/*"
            id="logo-upload"
            type="file"
            style={{ display: 'none' }}
            onChange={handleLogoUpload}
          />
          <label htmlFor="logo-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Image />}
              size="small"
            >
              {translations.store_creation_section.upload_logo || "Upload Logo"}
            </Button>
          </label>
          {address.logo && (
            <Box mt={2} display="flex" flexDirection="column" alignItems="center" width="100%">
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  padding: 2,
                  width: '100%',
                  maxWidth: '300px',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <img
                  src={address.logo}
                  alt="Store Logo"
                  style={{ maxWidth: '100%', maxHeight: '150px' }}
                />
              </Box>
              <Button
                variant="text"
                color="error"
                onClick={() => setAddress({ ...address, logo: '' })}
                size="small"
                sx={{ mt: 1 }}
              >
                {translations.store_creation_section.remove || "Remove"}
              </Button>
            </Box>
          )}
        </Box>
      </FormGrid>
      <FormGrid size={{ xs: 12 }}>
        <FormLabel htmlFor="address1" required>
          {translations.store_creation_section.address1}
        </FormLabel>
        <OutlinedInput
          id="address1"
          name="address1"
          type="address1"
          placeholder={translations.store_creation_section.address1_placeholder}
          autoComplete="shipping address-line1"
          required
          size="small"
          value={address.address1}
          onChange={(e) => setAddress({ ...address, address1: e.target.value })}
        />
      </FormGrid>
      <FormGrid size={{ xs: 12 }}>
        {translations.store_creation_section.address2}
        <OutlinedInput
          id="address2"
          name="address2"
          type="address2"
          placeholder={translations.store_creation_section.address2_placeholder}
          autoComplete="shipping address-line2"
          size="small"
          value={address.address2}
          onChange={(e) => setAddress({ ...address, address2: e.target.value })}
        />
      </FormGrid>
      <FormGrid size={{ xs: 4 }}>
        <FormLabel htmlFor="city" required>
          {translations.store_creation_section.city}
        </FormLabel>
        <OutlinedInput
          id="city"
          name="city"
          type="city"
          placeholder={translations.store_creation_section.city_placeholder}
          autoComplete="City"
          required
          size="small"
          value={address.city}
          onChange={(e) => setAddress({ ...address, city: e.target.value })}
        />
      </FormGrid>
      <FormGrid size={{ xs: 4 }}>
        <FormLabel htmlFor="postalCode" required>
          {translations.store_creation_section.postal_code}
        </FormLabel>
        <OutlinedInput
          id="postalCode"
          name="postalCode"
          type="postalCode"
          placeholder={translations.store_creation_section.postal_code_placeholder}
          required
          size="small"
          value={address.postalCode}
          onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
        />
      </FormGrid>
      <FormGrid size={{ xs: 4 }}>
        <FormLabel htmlFor="country" required>
          {translations.store_creation_section.country}
        </FormLabel>
        <OutlinedInput
          id="country"
          name="country"
          type="country"
          placeholder={translations.store_creation_section.country_placeholder}
          autoComplete="shipping country"
          required
          size="small"
          value={address.country}
          onChange={(e) => setAddress({ ...address, country: e.target.value })}
        />
      </FormGrid>
    </Grid>
  );
};

export default AddressForm;